import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import CustomersList from "./display/CustomersList";
import CustomersPageToolbar from "./display/CustomersPageToolbar";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import ProfileForm from "../../shared/profileForm/ProfileForm";
import ScrollableListPageLayout from "../../common/ScrollableListPageLayout";
import { CustomersDataProvider, useCustomersData } from "../../../contexts/app/customersData";
import ListPagination from "../../common/Pagination";

function CustomersPageInner() {
  const { hasPermission } = usePermissions();

  const {
    customers,
    selectedCustomer,
    profileFormOpen,
    isDesync,
    openProfile,
    openCreateProfile,
    closeProfile,
    updateCustomerInList,
    handleCustomerCreated,
    handleDeleteCustomer,
    setSelectedCustomer,
    listPage,
    setListPage,
    listTotal,
    listTotalPages,
    listPageSize,
    setListPageSize,
    listLoading,
    searchQuery,
    setSearchQuery,
    qualificationFilter,
    setQualificationFilter,
    qualificationOptions,
    isListQueryActive,
  } = useCustomersData();

  const canCreateCustomer = hasPermission(Permission.CUSTOMERS_PROFILE_EDIT);

  const customerUpdateWrapper = React.useCallback(
    (
      value:
        | ZekayCustomer
        | undefined
        | ((prev: ZekayCustomer | undefined) => ZekayCustomer | undefined),
    ) => {
      if (typeof value === "function") {
        const result = value(selectedCustomer);
        if (result) updateCustomerInList(result);
        else setSelectedCustomer(result);
      } else if (value) {
        updateCustomerInList(value);
      } else {
        setSelectedCustomer(value);
      }
    },
    [selectedCustomer, updateCustomerInList, setSelectedCustomer],
  );

  const searchHeader = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        pb: 1,
        px: { xs: 0.5, sm: 0 },
      }}
    >
      <CustomersPageToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        qualificationFilter={qualificationFilter}
        setQualificationFilter={setQualificationFilter}
        qualificationOptions={qualificationOptions}
        canCreate={Boolean(isDesync && canCreateCustomer)}
        onOpenCreate={openCreateProfile}
      />
    </Box>
  );

  const hasPagination = isListQueryActive && listTotalPages > 0 && listTotal > 0;

  return (
    <>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <ScrollableListPageLayout
          sx={{ flex: 1, minHeight: 0, height: "100%" }}
          header={searchHeader}
          pagination={
            hasPagination && !listLoading ? (
              <ListPagination
                currentPage={listPage}
                totalPages={listTotalPages}
                totalItems={listTotal}
                itemsPerPage={listPageSize}
                onPageChange={(p) => {
                  if (p >= 1 && p <= listTotalPages) setListPage(p);
                }}
                onItemsPerPageChange={(n) => {
                  setListPageSize(n);
                  setListPage(1);
                }}
                loading={listLoading}
              />
            ) : null
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {listLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size="md" color="primary" />
              </Box>
            )}
            {!listLoading && (
              <CustomersList
                customers={customers}
                onOpenProfile={openProfile}
                desyncMode={isDesync}
                onDelete={isDesync ? handleDeleteCustomer : undefined}
              />
            )}
          </Box>
        </ScrollableListPageLayout>
      </Box>

      {profileFormOpen && selectedCustomer && (
        <ProfileForm
          customer={selectedCustomer}
          setProfileFormOpen={closeProfile}
          setCustomerUpdated={customerUpdateWrapper}
          editable={isDesync}
          desync={isDesync}
          onCustomerCreated={handleCustomerCreated}
        />
      )}
    </>
  );
}

const CustomersPage: React.FC = React.memo(() => (
  <CustomersDataProvider>
    <CustomersPageInner />
  </CustomersDataProvider>
));

CustomersPage.displayName = "CustomersPage";

export default CustomersPage;
